import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Button } from "./ui/button";
import { Blog } from "@/types";
import { BlogRepository } from "@/infrastructure/repositories/BlogRepository";

interface BlogDetailPageProps {
  blogId: number;
  slug: string;
  onNavigate: (page: string) => void;
}

export function BlogDetailPage({ blogId, slug, onNavigate }: BlogDetailPageProps) {
  const blogRepo = new BlogRepository();
  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";

  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = (url: string, page: string, onNavigate: (p: string) => void) => {
    window.history.pushState({}, "", url);
    onNavigate(page);
  };

  // -------------------------------
  // API → UI BLOG MAPPER
  // -------------------------------
  const mapApiBlogToBlog = (apiBlog: any): Blog => ({
    id: Number(apiBlog.post_id),
    slug: apiBlog.post_slug ?? "",
    title: apiBlog.post_title ?? "",
    excerpt: apiBlog.post_meta_desc ?? "",
    content: apiBlog.post_desc ?? "",
    category: apiBlog.cat_name ?? "",
    author: apiBlog.author ?? "Admin",
    date: apiBlog.post_created_at
      ? new Date(Number(apiBlog.post_created_at) * 1000).toLocaleDateString()
      : "",
    readTime: apiBlog.read_time
      ? String(apiBlog.read_time)
      : "5 min read",
    image: apiBlog.post_img
      ? `${apiBlog.post_img}`
      : "",
    featured: false,
  });

  // -------------------------------
  // LOAD BLOG FROM LIST API ONLY
  // -------------------------------
  useEffect(() => {
  const loadBlog = async () => {
    setLoading(true);

    const cat19 = await blogRepo.getBlogList(1, 100, "19");
    const cat20 = await blogRepo.getBlogList(1, 100, "20");
    const list = Array.from(
      new Map([...cat19, ...cat20].map((b: any) => [String(b.post_id), b])).values()
    );

    const matched = list.find(
      (b: any) =>
        Number(b.post_id) === blogId ||
        b.post_slug === slug
    );

    if (!matched) {
      setBlog(null);
      setLoading(false);
      return;
    }

    setBlog(mapApiBlogToBlog(matched));

    // Ensure canonical URL
    const correctUrl = `/blog/${matched.post_id}/${matched.post_slug}`;
    if (window.location.pathname !== correctUrl) {
      window.history.replaceState({}, "", correctUrl);
    }

    setLoading(false);
  };

  loadBlog();
}, [blogId, slug]);


  // -------------------------------
  // STATES
  // -------------------------------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4">
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Blog not found
      </div>
    );
  }

  // -------------------------------
  // UI
  // -------------------------------
  return (
    <div className="min-h-screen bg-white">
      {/* HEADER */}
      <header className="border-b sticky top-0 z-50 bg-white/80 backdrop-blur">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <img
            src={logoSrc}
            alt="Swimnerd Logo"
            className="h-12 cursor-pointer"
            onClick={() => navigate("/", "landing", onNavigate)}
          />

          <div className="flex gap-4">
            <Button variant="ghost" onClick={() => navigate("/find-pool", "find-pool", onNavigate)}>
              Find Pool
            </Button>
            <Button variant="ghost" onClick={() => navigate("/blog", "blog", onNavigate)}>
              Blog
            </Button>
            <Button variant="ghost" onClick={() => navigate("/signin", "signin", onNavigate)}>
              Sign In
            </Button>
            <Button onClick={() => navigate("/signup", "signup", onNavigate)}>
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="h-[520px] overflow-hidden">
          <img
            src={blog.image}
            alt={blog.title}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
        </div>

        <div className="absolute inset-0 flex items-end">
          <div className="max-w-4xl mx-auto px-6 pb-16 text-white">
            <span className="inline-block mb-4 rounded-full bg-white/20 px-4 py-1 text-xs font-semibold">
              {blog.category}
            </span>

            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {blog.title}
            </h1>

            <p className="max-w-2xl text-lg text-white/90 mb-8">
              {blog.excerpt}
            </p>

            <div className="flex items-center gap-4 text-sm text-white/90">
              <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center text-lg font-semibold">
                {blog.author.charAt(0)}
              </div>
              <div>
                <div className="font-medium">{blog.author}</div>
                <div className="text-xs">
                  {blog.date} · {blog.readTime}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ARTICLE */}
      <main className="bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="bg-white rounded-3xl border p-10 lg:p-14">
            <article
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </div>
        </div>
      </main>
    </div>
  );
}


